import type { RouterOutputs } from 'shared/api'
import { useTranslate } from 'shared/lib/i18n'
import { som as money } from 'shared/lib/money'

/** The Shadow screen's shapes and words (ticket 22), apart from its components. */

export type ShadowFeed = RouterOutputs['shadow']['FeedStatus']
export type ShadowGapRow = RouterOutputs['shadow']['Gaps'][number]
export type ShadowNights = RouterOutputs['shadow']['Reconciliations']
export type ShadowNight = RouterOutputs['shadow']['ReconciliationDay']
export type ShadowDifference = ShadowNight['differenceRows'][number]
export type ShadowCause = NonNullable<ShadowDifference['explanation']>['cause']
export type ShadowFile = RouterOutputs['shadow']['ReconciliationExport']

export const SHADOW_CAUSES: readonly ShadowCause[] = ['tezgo_bug_fixed', 'back_gap', 'translator_gap']

/** A night's day as the panel writes a date: `2026-10-19` → `19.10.2026`. */
export const dayWords = (day: string) => day.split('-').reverse().join('.')

/** The words a refusal of the explanation is read in, by its code. */
export function explainFailureKey(error: unknown): 'shadow.commentRequired' | 'shadow.differenceGone' | null {
	const code = (error as { data?: { code?: string } } | null)?.data?.code
	if (code === 'BAD_REQUEST') return 'shadow.commentRequired'
	if (code === 'NOT_FOUND') return 'shadow.differenceGone'
	return null
}

/** One side of a difference, in words. */
export function useSideWords() {
	const { t } = useTranslate()
	const som = (n: number) => money(t, n)
	return (kind: ShadowDifference['kind'], side: ShadowDifference['back']): string => {
		if (!side) return t('shadow.nothing')
		if (kind === 'cash') {
			if (!side.cash) return t('shadow.nothing')
			return t('shadow.cashLine', {
				payments: String(side.cash.payments.count),
				paymentsSum: som(side.cash.payments.uzs),
				cancellations: String(side.cash.cancellations.count),
				cancellationsSum: som(side.cash.cancellations.uzs),
			})
		}
		const parts = [
			side.state,
			side.pointId
				? t('shadow.atPoint', { point: side.pointId })
				: side.where
					? t('shadow.notAtPoint', { where: t(`shadowWhere.${side.where}` as never) })
					: null,
			side.paid === null
				? null
				: side.paid
					? side.paymentType
						? t('shadow.paidAs', { type: side.paymentType })
						: t('shadow.paid')
					: t('shadow.unpaid'),
		]
		return parts.filter(Boolean).join(', ')
	}
}
